/**
 * Status Code Category Decorator
 *
 * The SOLE source of color for HTTP status codes. The syntax highlighting
 * intentionally does NOT color StatusCode nodes (omitted from styleTags),
 * so this decorator has full control.
 *
 * Colors by category:
 * - 1xx → info (blue)
 * - 2xx → green (success)
 * - 3xx → blue (redirect)
 * - 4xx → orange (client error)
 * - 5xx → red (server error)
 */

import { syntaxTree, ensureSyntaxTree } from '@codemirror/language';
import { Decoration, ViewPlugin } from '@codemirror/view';
import type { DecorationSet, ViewUpdate, EditorView } from '@codemirror/view';
import type { Extension, Range } from '@codemirror/state';

export type StatusCodeColors = {
  info: string;
  success: string;
  redirect: string;
  clientError: string;
  serverError: string;
};

function createStatusMarks(colors: StatusCodeColors) {
  return {
    '1': Decoration.mark({
      attributes: {
        style: `color: ${colors.info}; font-weight: 600`,
      },
    }),
    '2': Decoration.mark({
      attributes: {
        style: `color: ${colors.success}; font-weight: 600`,
      },
    }),
    '3': Decoration.mark({
      attributes: {
        style: `color: ${colors.redirect}; font-weight: 600`,
      },
    }),
    '4': Decoration.mark({
      attributes: {
        style: `color: ${colors.clientError}; font-weight: 600`,
      },
    }),
    '5': Decoration.mark({
      attributes: {
        style: `color: ${colors.serverError}; font-weight: 600`,
      },
    }),
  } as const;
}

type StatusMarks = ReturnType<typeof createStatusMarks>;

function buildDecorations(view: EditorView, marks: StatusMarks): DecorationSet {
  const tree =
    ensureSyntaxTree(view.state, view.state.doc.length, 500) ??
    syntaxTree(view.state);

  const ranges: Range<Decoration>[] = [];

  tree.iterate({
    enter(node) {
      if (node.name === 'StatusCode') {
        const text = view.state.sliceDoc(node.from, node.to);
        const firstDigit = text.charAt(0) as keyof StatusMarks;
        const mark = marks[firstDigit];
        if (mark) {
          ranges.push(mark.range(node.from, node.to));
        }
      }
    },
  });

  return Decoration.set(ranges, true);
}

export function statusCodeDecorator(colors: StatusCodeColors): Extension {
  const marks = createStatusMarks(colors);

  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = buildDecorations(view, marks);
      }

      update(update: ViewUpdate) {
        if (
          update.docChanged ||
          update.viewportChanged ||
          syntaxTree(update.state) !== syntaxTree(update.startState)
        ) {
          this.decorations = buildDecorations(update.view, marks);
        }
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );
}
